/**
 * "Bend luxury homes for sale" has ONE winner: /homes-for-sale/bend/luxury
 * (the city x `luxury` preset search, PAGE_OUTLINE one-winner, SITE-185).
 *
 * GSC 2026-08-24..09-20, queries containing "bend luxury": 224 impressions,
 * 0 clicks, split /luxury-homes-bend 64% (pos 27.5), /housing-market/bend 31%
 * (the appreciation queries, which that page keeps), a Caldera Springs post
 * 2%, a Moonshadow listing 1%. The winner held 0% because nothing linked it:
 * every "Luxury homes in Bend" door in the chrome, the footer, the city and
 * communities pages went to /luxury-homes-bend, which 308'd onto the Bend
 * search with a ?minPrice query string, and the legacy /luxury-homes-bend-oregon
 * chain folded that query string into a path and died on "City not found".
 *
 * This module is the one place the winner's path, its door label, and its
 * title / h1 phrase are typed. Nothing hand-lists the path elsewhere; the
 * redirect sources (next.config.ts, data/legacy-redirects.json) carry the same
 * literal because neither can import from lib/.
 */
import type { SearchPreset } from '@/lib/search-presets'
import { PUBLIC_PHONE } from '@/lib/brand/contact'
import { FOR_SALE_MARKER, INLINE_LINK_RE, stripInlineLinks } from '@/lib/site/place-answer-box/copy'

export const BEND_LUXURY_HOMES_PATH = '/homes-for-sale/bend/luxury'

/** The phrase people search, as anchor text. */
export const BEND_LUXURY_HOMES_LABEL = 'Bend luxury homes for sale'


export const LUXURY_PRESET_SLUG = 'luxury'

/** The sitewide door: chrome, footer, city and community edges all use it. */
export function bendLuxuryHomesDoor(): { href: string; label: string } {
  return { href: BEND_LUXURY_HOMES_PATH, label: BEND_LUXURY_HOMES_LABEL }
}

export function isLuxuryPreset(preset: Pick<SearchPreset, 'slug'> | null | undefined): boolean {
  return preset?.slug === LUXURY_PRESET_SLUG
}

/**
 * The win query in human form for a `luxury` preset page: "{Place} luxury
 * homes for sale". Title and h1 share it (PAGE_OUTLINE Title / H1 rules:
 * place name first, never two pages with the same h1 - the place makes each
 * one distinct). Null for every other preset, so callers keep their own
 * composition.
 */
export function luxuryPresetHeading(
  preset: Pick<SearchPreset, 'slug'> | null | undefined,
  placeName: string,
): string | null {
  if (!isLuxuryPreset(preset)) return null
  const place = placeName.trim()
  if (!place) return 'Luxury homes for sale'
  return `${place} luxury homes for sale`
}

/** CTR fix 17 (Writing Bot draft, Oct 22, 2026): the Bend winner's title. */
export const BEND_LUXURY_HOMES_TITLE = 'Bend Luxury Homes for Sale'

function isBend(placeName: string): boolean {
  return placeName.trim() === 'Bend'
}

/**
 * Document title for a `luxury` preset page. The Bend winner takes the CTR
 * fix 17 title case form; its h1 stays luxuryPresetHeading. Other places keep
 * the heading as their title.
 */
export function luxuryPresetTitle(
  preset: Pick<SearchPreset, 'slug'> | null | undefined,
  placeName: string,
): string | null {
  const heading = luxuryPresetHeading(preset, placeName)
  if (!heading) return null
  return isBend(placeName) ? BEND_LUXURY_HOMES_TITLE : heading
}

/**
 * Meta description for a `luxury` preset page. Opens on the query. The floor
 * is the preset's own `minPrice` filter parameter (lib/search-presets.ts), a
 * filter definition rather than a market figure, so it needs no data trace.
 * The Bend winner carries the CTR fix 17 copy (same floor, public phone from
 * lib/brand/contact); with no floor it keeps the shared form.
 */
export function luxuryPresetDescription(
  preset: Pick<SearchPreset, 'slug' | 'params'> | null | undefined,
  placeName: string,
): string | null {
  const heading = luxuryPresetHeading(preset, placeName)
  if (!heading || !preset) return null
  const minPrice = preset.params.minPrice
  const floorText =
    typeof minPrice === 'number' && Number.isFinite(minPrice) && minPrice > 0 ? formatFloor(minPrice) : null
  if (isBend(placeName) && floorText) {
    return `Bend luxury homes for sale priced ${floorText} or more. See Broken Top, Tetherow, Crosswater, Juniper Preserve, Caldera Springs. Live MLS. ${PUBLIC_PHONE.display}.`
  }
  const floor = floorText ? ` priced ${floorText} and up` : ''
  return `${heading}: live luxury real estate listings in ${placeName.trim()}${floor}, from the regional MLS, with price, size, and the map.`
}

function formatFloor(value: number): string {
  if (value >= 1_000_000 && value % 100_000 === 0) {
    const millions = value / 1_000_000
    return `$${Number.isInteger(millions) ? millions : millions.toFixed(1)} million`
  }
  return `$${value.toLocaleString('en-US')}`
}


/**
 * Trends 21 pack (docs/ops/trends-21-luxury-2026-10-28): answer-first and four
 * FAQs for the Bend winner, pasted verbatim. {forSale} takes the page's live
 * count sentence (dropped when there is none), never a saved number.
 */
const PHONE = PUBLIC_PHONE.display
const PACK_ANSWER =
  `Bend luxury homes for sale are active MLS listings in Bend priced at $2,000,000 or more. Counts move with the regional MLS, so use the live list on this page. Compare communities such as [Broken Top](/communities/broken-top), [Tetherow](/communities/tetherow), [Crosswater](/communities/crosswater), [Juniper Preserve](/communities/juniper-preserve), and [Caldera Springs](/communities/caldera-springs). For the regional buying guide, see [Central Oregon luxury homes](/buy/luxury). ${FOR_SALE_MARKER} Call Ryan Realty at ${PHONE}. Want controlled entry too? See [gated community homes for sale in Bend](/communities/bend-gated-communities).`
const PACK_FAQS: { question: string; answer: string }[] = [
  {
    question: 'How many luxury homes are for sale in Bend?',
    answer: `This page lists live MLS homes in Bend priced at $2,000,000 or more. Counts move with the market, so use the list here rather than a saved number. ${FOR_SALE_MARKER} Save a search or call ${PHONE}.`,
  },
  {
    question: 'What counts as a luxury home on this page?',
    answer: 'On Ryan Realty, luxury inventory means list price $2,000,000 or more. For the Central Oregon buying guide, see [/buy/luxury](/buy/luxury).',
  },
  {
    question: 'Where else should I look for $2M+ homes in Bend?',
    answer:
      'Start with community hubs for [Broken Top](/communities/broken-top), [Tetherow](/communities/tetherow), [Crosswater](/communities/crosswater), [Juniper Preserve](/communities/juniper-preserve), and [Caldera Springs](/communities/caldera-springs). The same Bend $2M+ filter with a different label is at [/homes-for-sale/bend/over-2m](/homes-for-sale/bend/over-2m).',
  },
  {
    question: 'How do I get new $2M+ Bend listings?',
    answer: `Set a listing alert from [/buy/alerts](/buy/alerts) and choose $2,000,000 as your starting price. New matches come to your inbox. Or call Ryan Realty at ${PHONE}.`,
  },
]

function linksOf(text: string): { text: string; href: string }[] {
  return Array.from(text.matchAll(new RegExp(INLINE_LINK_RE.source, 'g'))).map((m) => ({ text: m[1], href: m[2] }))
}

function fillSale(text: string, sentence: string | null): string {
  return text.split(FOR_SALE_MARKER).join(sentence ?? '').replace(/\s{2,}/g, ' ').trim()
}

/** Plain text plus inline-link lists (the page renders links; JSON-LD stays plain). */
export function bendLuxuryPackCopy(forSale: string | null): {
  intro: string
  introLinks: { text: string; href: string }[]
  faqs: { question: string; answer: string; links: { text: string; href: string }[] }[]
} {
  const answer = fillSale(PACK_ANSWER, forSale)
  return {
    intro: stripInlineLinks(answer),
    introLinks: linksOf(answer),
    faqs: PACK_FAQS.map((f) => {
      const filled = fillSale(f.answer, forSale)
      return { question: f.question, answer: stripInlineLinks(filled), links: linksOf(filled) }
    }),
  }
}
